/**
 * Cordova / native shell helpers. Sets --native-safe-area-top when env()
 * insets are unavailable in WKWebView so sticky headers clear the notch.
 */

export function isNativeApp(): boolean {
  const w = window as any;
  return !!(w.cordova || w.Cordova);
}

export function isIosNative(): boolean {
  const w = window as any;
  return isNativeApp() && (w.cordova?.platformId === 'ios' || w.device?.platform === 'iOS');
}

export function validateTrustedHttpsUrl(url: string, allowedHosts: readonly string[]): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error('External URL is invalid');
  }

  const hostname = parsed.hostname.toLowerCase().replace(/\.$/, '');
  const hostAllowed = allowedHosts.some((allowedHost) => {
    const normalized = allowedHost.toLowerCase().replace(/\.$/, '');
    return normalized.startsWith('*.')
      ? hostname.endsWith(normalized.slice(1)) && hostname !== normalized.slice(2)
      : hostname === normalized;
  });

  if (parsed.protocol !== 'https:'
    || parsed.username
    || parsed.password
    || (parsed.port && parsed.port !== '443')
    || !hostAllowed) {
    throw new Error('External URL is not an allowed HTTPS destination');
  }

  return parsed.toString();
}

export const RESUME_FILE_ALLOWED_HOSTS = Object.freeze(['s3.amazonaws.com']);
const RESUME_FILE_ALLOWED_PATH_PREFIXES = Object.freeze([
  '/tempstars.ca/',
  '/dev-tempstars.ca/',
]);

export function validateResumeFileUrl(url: string): string {
  const destination = validateTrustedHttpsUrl(url, RESUME_FILE_ALLOWED_HOSTS);
  const parsed = new URL(destination);
  if (!RESUME_FILE_ALLOWED_PATH_PREFIXES.some((prefix) => parsed.pathname.startsWith(prefix))) {
    throw new Error('Resume URL is outside the TempStars file bucket');
  }
  return destination;
}

export function openExternalUrl(url: string, allowedHosts?: readonly string[]): void {
  const destination = allowedHosts ? validateTrustedHttpsUrl(url, allowedHosts) : url;
  const cordova = (window as any).cordova;
  if (cordova?.InAppBrowser?.open) {
    cordova.InAppBrowser.open(destination, '_system', 'location=yes');
    return;
  }

  window.open(destination, '_blank', 'location=yes');
}

type ExternalLinkEvent = Pick<Event, 'preventDefault' | 'stopPropagation'>;

/**
 * Open an anchor through the native-safe external browser path while preventing
 * Framework7's document click router from swallowing the link inside Cordova.
 */
export function openExternalUrlFromLink(
  event: ExternalLinkEvent,
  url: string,
  allowedHosts?: readonly string[],
): void {
  event.preventDefault();
  event.stopPropagation();
  openExternalUrl(url, allowedHosts);
}

export function measureSafeAreaTop(): number {
  if (typeof document === 'undefined') return 0;

  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;padding-top:constant(safe-area-inset-top);padding-top:env(safe-area-inset-top);pointer-events:none;visibility:hidden;';
  document.documentElement.appendChild(probe);
  const measured = parseFloat(window.getComputedStyle(probe).paddingTop) || 0;
  document.documentElement.removeChild(probe);
  return measured;
}

function fallbackIosSafeAreaTop(): number {
  const w = window as any;
  const isIos = w.cordova?.platformId === 'ios' || w.device?.platform === 'iOS';
  if (!isIos) return 0;

  const logicalMin = Math.min(window.screen.width, window.screen.height);
  // iPhone X+ / Dynamic Island class devices
  if (logicalMin >= 390) return 47;
  if (logicalMin >= 375) return 44;
  return 20;
}

function fallbackAndroidSafeAreaTop(): number {
  const w = window as any;
  const isAndroid = w.cordova?.platformId === 'android' || w.device?.platform === 'Android';
  if (!isAndroid) return 0;

  // Some Android Cordova builds render edge-to-edge while env() still reports 0.
  return 24;
}

export function resolveNativeSafeAreaTop(): number {
  const measured = measureSafeAreaTop();
  if (measured > 0) return measured;
  return fallbackIosSafeAreaTop() || fallbackAndroidSafeAreaTop();
}

export function applyNativeSafeAreaCssVar(): void {
  const top = resolveNativeSafeAreaTop();
  document.documentElement.style.setProperty('--native-safe-area-top', `${top}px`);
  document.body.classList.add('native-app');

  const w = window as any;
  const platformId = String(w.cordova?.platformId || w.device?.platform || '').toLowerCase();
  document.body.classList.toggle('platform-android', platformId === 'android');
  document.body.classList.toggle('platform-ios', platformId === 'ios');

  if (w.cordova) {
    w.Cordova = true;
  }
}

/** Call once at app boot; safe to invoke before or after deviceready. */
export function initNativeAppShell(): void {
  const w = window as any;
  if (!w.cordova) return;

  const apply = () => {
    applyNativeSafeAreaCssVar();
    window.setTimeout(() => {
      const remeasured = resolveNativeSafeAreaTop();
      document.documentElement.style.setProperty('--native-safe-area-top', `${remeasured}px`);
    }, 150);
  };

  if (w.device?.platform) {
    apply();
  } else {
    document.addEventListener('deviceready', apply, { once: true });
  }
}

export const ANDROID_LOADING_ROUTE = 'loading';

export type AndroidBackAction =
  | { type: 'back'; steps: number }
  | { type: 'replace'; pathname: string }
  | { type: 'exit' };

const ANDROID_BACK_ROOT_ROUTES = new Set([
  'dentistHome',
  'hygienistHome',
  'jobPostsList',
  'landing',
  ANDROID_LOADING_ROUTE,
]);

const ANDROID_BACK_AUTHENTICATED_HOME_ROUTES = new Set([
  'dentistHome',
  'hygienistHome',
  'jobPostsList',
]);

const ANDROID_BACK_FALLBACK_ROUTES: Record<string, string> = {
  availableJobs: 'hygienistHome',
  availableJob: 'hygienistHome',
  partialOffer: 'hygienistHome',
  confirmedOffer: 'hygienistHome',
  hygienistInstabookConfirmation: 'hygienistHome',
  hygienistMessenger: 'hygienistHome',
  completeJob: 'hygienistHome',
  hygienistProfile: 'hygienistHome',
  forcedQualification: 'hygienistHome',
  hygienistInvoices: 'hygienistHome',
  hygienistInvoice: 'hygienistHome',
  manualBookShift: 'hygienistHome',
  myStats: 'hygienistHome',
  hygienistFeedback: 'hygienistHome',
  autoBook: 'hygienistHome',
  coursesHome: 'hygienistHome',
  courseDetails: 'coursesHome',
  coursePlayer: 'coursesHome',
  courseReturn: 'coursesHome',
  jobPostDetails: 'jobPostsList',
  dentistInvoices: 'dentistHome',
  dentistLocations: 'dentistHome',
  locationAdd: 'dentistLocations',
  locationDetails: 'dentistLocations',
  paymentInfo: 'dentistHome',
  jobBoard: 'dentistHome',
  jobBoardDetails: 'jobBoard',
  candidate: 'jobBoard',
  candidateResume: 'candidate',
  jobBoardCandidate: 'jobBoard',
  jobFeedback: 'jobBoard',
  postPosition: 'jobBoard',
  postOfficeLocation: 'postPosition',
  postAvailability: 'postOfficeLocation',
  postDates: 'postAvailability',
  postCandidateRequirements: 'postDates',
  postTitleDescription: 'postCandidateRequirements',
  postConfirmation: 'postTitleDescription',
  postSuccess: 'dentistHome',
  customOfferDetails: 'dentistHome',
  dentistCustomOffers: 'dentistHome',
  hygienistCancelJob: 'dentistHome',
  jobPosted: 'dentistHome',
  jobCompleted: 'dentistHome',
  feedbackPage: 'jobCompleted',
  editJobFirstStep: 'jobPosted',
  editJobSecondStep: 'editJobFirstStep',
  editJobOverviewStep: 'editJobSecondStep',
  postJob: 'dentistHome',
  dentistJobsForDate: 'dentistHome',
  invoice: 'dentistHome',
  dentistJobConfirmed: 'jobPosted',
  messenger: 'dentistHome',
  customOffer: 'dentistHome',
  modifyJob: 'jobPosted',
  hygienistContactSupport: 'hygienistHome',
  dentistContactSupport: 'dentistHome',
  hygienistDeleteAccount: 'hygienistHome',
  hygienistDeleteAccountSecondStep: 'hygienistDeleteAccount',
  dentistDeleteAccount: 'dentistHome',
};

const androidBackOverrides: Array<{ handler: () => void }> = [];

export function normalizeReactRoute(pathname?: string): string {
  return String(pathname || '').replace(/^\/+/, '');
}

export function isAuthenticatedHomeRoute(pathname?: string): boolean {
  return ANDROID_BACK_AUTHENTICATED_HOME_ROUTES.has(normalizeReactRoute(pathname));
}

export function isAndroidLoadingRoute(pathname?: string): boolean {
  return normalizeReactRoute(pathname) === ANDROID_LOADING_ROUTE;
}

/** Preserve the current React screen beneath the bridge loading placeholder. */
export function pushLoadingRoute(history: { push: (path: string) => void }): void {
  history.push(ANDROID_LOADING_ROUTE);
}

function findPreviousNonLoadingIndex(
  entries: Array<{ pathname?: string }> | undefined,
  index: number | undefined,
): number | null {
  if (!entries || typeof index !== 'number' || index <= 0) {
    return null;
  }

  let targetIndex = index - 1;
  while (targetIndex >= 0 && isAndroidLoadingRoute(entries[targetIndex]?.pathname)) {
    targetIndex -= 1;
  }

  return targetIndex >= 0 ? targetIndex : null;
}

export function getAndroidBackAction({
  pathname,
  historyLength,
  entries,
  index,
  returnToPathname,
  preferReturnToOnBack,
}: {
  pathname?: string;
  historyLength?: number;
  entries?: Array<{ pathname?: string }>;
  index?: number;
  returnToPathname?: string;
  preferReturnToOnBack?: boolean;
}): AndroidBackAction {
  const route = normalizeReactRoute(pathname);
  const explicitReturnRoute = normalizeReactRoute(returnToPathname);
  const previousNonLoadingIndex = findPreviousNonLoadingIndex(entries, index);

  if (
    preferReturnToOnBack === true &&
    isAuthenticatedHomeRoute(explicitReturnRoute)
  ) {
    if (
      previousNonLoadingIndex != null &&
      normalizeReactRoute(entries?.[previousNonLoadingIndex]?.pathname) === explicitReturnRoute
    ) {
      return { type: 'back', steps: (index as number) - previousNonLoadingIndex };
    }
    return { type: 'replace', pathname: explicitReturnRoute };
  }

  if (ANDROID_BACK_ROOT_ROUTES.has(route)) {
    return { type: 'exit' };
  }

  if (entries && typeof index === 'number' && index > 0) {
    if (previousNonLoadingIndex == null) {
      return { type: 'exit' };
    }

    const steps = index - previousNonLoadingIndex;
    // Deep-link / cold-open stacks are shallow (typically landing + target).
    // Use replace fallback there so Back does not later go "forward" again.
    // When loading placeholders sit in the stack, steps > 1 still means goBack.
    if ((historyLength || 0) > 2 || steps > 1) {
      return { type: 'back', steps };
    }
  } else if ((historyLength || 0) > 2) {
    return { type: 'back', steps: 1 };
  }

  const fallback = ANDROID_BACK_FALLBACK_ROUTES[route];
  if (fallback) {
    return { type: 'replace', pathname: fallback };
  }

  return { type: 'exit' };
}

export function registerAndroidBackOverride(handler: () => void): () => void {
  if (!isNativeApp()) return () => {};

  const registration = { handler };
  androidBackOverrides.push(registration);

  return () => {
    const index = androidBackOverrides.indexOf(registration);
    if (index !== -1) androidBackOverrides.splice(index, 1);
  };
}

export function exitNativeAppFromAndroidBack(): void {
  const navigatorApp = (window.navigator as any)?.app;
  if (typeof navigatorApp?.exitApp === 'function') {
    navigatorApp.exitApp();
    return;
  }
  if (typeof navigatorApp?.backHistory === 'function') {
    navigatorApp.backHistory();
  }
}

export function registerAndroidBackHandler({
  getAction,
  onBack,
  onReplace,
  onExit,
}: {
  getAction: () => AndroidBackAction;
  onBack: (steps: number) => void;
  onReplace: (pathname: string) => void;
  onExit?: () => void;
}): () => void {
  if (!isNativeApp()) return () => {};

  const handleBackButton = (event: Event) => {
    event.preventDefault();

    const override = androidBackOverrides[androidBackOverrides.length - 1];
    if (override) {
      override.handler();
      return;
    }

    const action = getAction();
    if (action.type === 'back') {
      onBack(action.steps);
      return;
    }
    if (action.type === 'replace') {
      onReplace(action.pathname);
      return;
    }
    (onExit || exitNativeAppFromAndroidBack)();
  };

  document.addEventListener('backbutton', handleBackButton, false);

  return () => {
    document.removeEventListener('backbutton', handleBackButton, false);
  };
}

export function blurActiveElementAndHideKeyboard(): void {
  const active = document.activeElement as HTMLElement | null;
  active?.blur?.();

  const keyboard = (window as any).Keyboard || (window as any).cordova?.plugins?.Keyboard;
  keyboard?.hide?.();
}
